import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { OrderItemView, OrderView, PreparationItemView, PreparationState } from '../../shared/types';
import { Badge, Button, Card, EmptyState, HeroSelectField, LoadingScreen, PageHeader, cx } from '../components/Ui';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useBusinessBranches, useDeliveryTokenMutation, useKitchenOrders, useKitchenZones, useOrders } from '../hooks/businessData';
import { useDeliveryLinks } from '../hooks/useDeliveryLinks';
import { useSse } from '../hooks/useSse';
import { ApiClientError } from '../lib/api';
import { apiRequest, jsonBody } from '../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import { formatLocalMinor, formatUsd } from '../lib/currency';
import { deliveryLinkUrl, deliveryWhatsAppUrl, isReadyDeliveryOrder } from '../lib/deliveryLink';

const prepLabels: Record<PreparationState, string> = {
  pendiente: 'Pendiente',
  preparacion: 'Preparando',
  completado: 'Listo',
};

function OptionChips({ options }: { options: OrderItemView['options'] }) {
  if (!options.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {options.map((option) => (
        <span key={option.id} className="rounded-full bg-sky-100 px-2.5 py-1 text-xs font-semibold text-sky-900">
          {option.groupName}: {option.quantity > 1 ? `${option.quantity}× ` : ''}{option.optionName}
        </span>
      ))}
    </div>
  );
}

/** Order-content snapshot so the courier can verify what leaves the branch. */
function DeliveryItemSnapshot({ item }: { item: OrderItemView }) {
  const excluded = item.ingredients.filter((ingredient) => ingredient.excluded);
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <strong>{item.quantity}× {item.productName}</strong>
        {item.variantName && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-bold text-violet-800">{item.variantName}</span>}
        {item.productType === 'combo' && <Badge tone="purple">combo</Badge>}
      </div>
      <OptionChips options={item.options} />
      {excluded.length > 0 && <p className="mt-2 text-xs font-bold text-rose-700">Sin: {excluded.map((ingredient) => ingredient.name).join(', ')}</p>}
      {item.components.length > 0 && (
        <div className="mt-2 grid gap-2 border-l-2 border-violet-200 pl-3">
          {item.components.map((component) => (
            <div key={component.id} className="rounded-lg bg-white p-2 ring-1 ring-slate-200">
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <span className="font-black text-violet-800">{component.componentName}</span>
                <span className="font-bold">{component.quantity}× {component.productName}</span>
                {component.variantName && <span className="rounded-full bg-violet-100 px-2 py-0.5 font-semibold text-violet-800">{component.variantName}</span>}
                <span className="text-slate-500">Zona {component.preparationZone}</span>
              </div>
              <OptionChips options={component.options} />
              {component.excludedIngredients.length > 0 && <p className="mt-2 text-xs font-bold text-rose-700">Sin: {component.excludedIngredients.map((ingredient) => ingredient.name).join(', ')}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Resolve a preparation task back to its order-item / component for detail display. */
function configurationForTask(order: OrderView, task: PreparationItemView) {
  const orderItem = order.items.find((item) => item.id === task.orderItemId) ?? null;
  const component = task.componentId
    ? orderItem?.components.find((item) => item.id === task.componentId) ?? null
    : null;
  return { orderItem, component };
}

/**
 * Mode-B prep task card: shows task details with a mark-done control.
 * Mirrors KitchenPage's TaskCard patterns for consistency.
 */
function PrepTaskCard({ order, task, busy, onStatus }: { order: OrderView; task: PreparationItemView; busy: boolean; onStatus(status: PreparationState): void }) {
  const canSeeDetails = task.status !== 'pendiente';
  const { orderItem, component } = configurationForTask(order, task);
  const options = component?.options ?? orderItem?.options ?? [];
  const ingredients = component?.ingredients ?? orderItem?.ingredients ?? [];
  const excluded = ingredients.filter((ingredient) => ingredient.excluded);
  const added = ingredients.filter((ingredient) => ingredient.included && ingredient.extraPriceUsdCents > 0);
  return (
    <Card className={cx('relative overflow-hidden p-0', task.status === 'preparacion' && 'ring-2 ring-amber-400')}>
      <div className="flex items-stretch">
        <div className="grid min-w-24 place-items-center bg-slate-950 p-3 text-center text-white">
          <div><div className="text-[10px] font-black uppercase tracking-[.2em] text-slate-400">Pedido</div><div className="text-4xl font-black leading-none">#{String(order.correlation).padStart(2, '0')}</div><div className="mt-2 text-xs text-slate-300">{new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div></div>
        </div>
        <div className="min-w-0 flex-1 p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="text-xl font-black">{canSeeDetails ? <><span className="mr-2 rounded-lg bg-slate-100 px-2 py-1">{task.quantity}×</span>{task.productName}</> : 'Tarea pendiente'}</h3>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-bold">
                {canSeeDetails && task.variantName && <span className="rounded-full bg-violet-100 px-2.5 py-1 text-violet-800">Variante: {task.variantName}</span>}
                {canSeeDetails && component && <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">{component.componentName} de {orderItem?.productName}</span>}
                <span className="uppercase tracking-wider text-slate-500">Zona: {task.preparationZone}</span>
              </div>
            </div>
            <Badge tone={task.status === 'completado' ? 'green' : task.status === 'preparacion' ? 'amber' : 'slate'}>{prepLabels[task.status]}</Badge>
          </div>
          {!canSeeDetails && <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm font-semibold text-slate-600">Iniciá la preparación para ver qué preparar.</p>}
          {canSeeDetails && options.length > 0 && <div className="mt-3 rounded-xl bg-sky-50 p-3 text-sm font-semibold text-sky-950"><strong>Opciones:</strong> {options.map((option) => `${option.groupName}: ${option.quantity > 1 ? `${option.quantity}× ` : ''}${option.optionName}`).join(' · ')}</div>}
          {canSeeDetails && excluded.length > 0 && <div className="mt-3 rounded-xl bg-rose-50 p-3 text-sm font-black text-rose-800">SIN: {excluded.map((ingredient) => ingredient.name).join(', ')}</div>}
          {canSeeDetails && added.length > 0 && <div className="mt-3 rounded-xl bg-emerald-50 p-3 text-sm font-black text-emerald-800">EXTRA: {added.map((ingredient) => ingredient.name).join(', ')}</div>}
          {canSeeDetails && order.notes && <p className="mt-2 rounded-xl bg-amber-50 p-2 text-sm text-amber-900"><strong>Nota:</strong> {order.notes}</p>}
          <div className="mt-4 flex flex-wrap gap-2">
            {task.status === 'pendiente' && <Button busy={busy} onClick={() => onStatus('preparacion')}>Comenzar</Button>}
            {task.status === 'preparacion' && <Button variant="success" busy={busy} onClick={() => onStatus('completado')}>✓ Marcar listo</Button>}
            {task.status === 'preparacion' && <Button variant="secondary" busy={busy} onClick={() => onStatus('pendiente')}>Devolver a pendiente</Button>}
          </div>
        </div>
      </div>
    </Card>
  );
}

/**
 * Mode-B delivery prep queue: shows delivery_prep orders with pending prep tasks
 * and mark-done controls for the delivery role.
 */
function DeliveryPrepQueue({ branchId, zone, busyTaskId, onStatus }: {
  branchId: string;
  zone: string;
  busyTaskId: string | null;
  onStatus: (order: OrderView, task: PreparationItemView, status: PreparationState) => void;
}) {
  const { data: orders = [], isLoading } = useKitchenOrders(branchId, zone);
  const loading = isLoading && !branchId;

  const prepOrders = useMemo(() => orders.filter(
    (order) => order.deliveryType === 'delivery' && order.fulfillmentStage === 'delivery_prep',
  ), [orders]);

  const queue = useMemo(() => prepOrders
    .flatMap((order) => order.preparationItems
      .filter((task) => task.status !== 'completado' && (!zone || task.preparationZone === zone))
      .map((task) => ({ order, task })))
    .sort((a, b) => a.order.createdAt.localeCompare(b.order.createdAt)), [prepOrders, zone]);

  if (loading) return <LoadingScreen label="Cargando cola de reparto…" />;

  return queue.length
    ? <div className="grid gap-4 xl:grid-cols-2">{queue.map(({ order, task }) => <PrepTaskCard key={task.id} order={order} task={task} busy={busyTaskId === task.id} onStatus={(status) => void onStatus(order, task, status)} />)}</div>
    : <EmptyState title="Cola de reparto al día" description={zone ? `No hay tareas pendientes en la zona "${zone}".` : 'No hay tareas pendientes de preparación de reparto.'} />;
}

/**
 * Internal delivery section:
 *  - Mode A (branch delivery_prep_mode='kitchen'): lists only `delivery`
 *    orders whose fulfillment stage is `ready`. The single-use token is
 *    captured at issue time into `useDeliveryLinks` state and never
 *    re-fetched — the WhatsApp button consumes the already-captured link
 *    through the shared delivery-link seam.
 *  - Mode B (branch delivery_prep_mode='delivery'): shows a delivery_prep
 *    queue (delivery-type orders in delivery_prep stage) with mark-done
 *    controls, reusing the Slice 1 hooks and the queue-scoped prep endpoint.
 */
export default function DeliveryStaffPage() {
  const { user } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const [branchId, setBranchId] = useState(user?.branchId ?? '');
  const [actionId, setActionId] = useState<string | null>(null);
  const [busyTaskId, setBusyTaskId] = useState<string | null>(null);
  const [zone, setZone] = useState('');
  const { links, setLink, copyLink } = useDeliveryLinks();

  const canChooseBranch = user?.role === 'owner' || user?.role === 'manager';

  const { data: allBranches = [] } = useBusinessBranches(canChooseBranch);
  const branches = useMemo(() => {
    if (!canChooseBranch && user?.branchId) {
      return [{ id: user.branchId, name: 'Sucursal asignada', active: true, whatsapp: null, deliveryPrepMode: 'kitchen' as const }];
    }
    return allBranches.filter((branch) => branch.active);
  }, [canChooseBranch, user?.branchId, allBranches]);

  useEffect(() => {
    if (!branchId && branches.length) setBranchId(branches[0].id);
  }, [branchId, branches]);

  const deliveryPrepMode = useMemo(() => {
    const branch = branches.find((b) => b.id === branchId);
    return branch?.deliveryPrepMode ?? 'kitchen';
  }, [branches, branchId]);

  const isModeB = deliveryPrepMode === 'delivery';

  // Mode A: fetch orders with multiple statuses (ready-only client filter)
  const { data: orders = [], isLoading, isFetching } = useOrders(
    branchId,
    isModeB ? 'preparacion' : 'pendiente,preparacion,completado,cancelado',
  );
  const loading = isLoading && !branches.length;
  const refreshing = isFetching && !isLoading;

  const { data: zones = [] } = useKitchenZones(branchId);

  useEffect(() => {
    if (zone && zones.length > 0 && !zones.includes(zone)) setZone('');
  }, [zone, zones]);

  const invalidateOrdersRef = useRef(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
  });
  useEffect(() => {
    invalidateOrdersRef.current = () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    };
  }, [queryClient]);
  const streamStatus = useSse(branchId, () => { invalidateOrdersRef.current(); });

  const readyOrders = useMemo(() => orders.filter(isReadyDeliveryOrder), [orders]);

  const tokenMutation = useDeliveryTokenMutation();

  const issueToken = useCallback(async (order: OrderView, action: 'issue' | 'rotate') => {
    setActionId(order.id);
    try {
      const result = await tokenMutation.mutateAsync({ orderId: order.id, expectedStage: 'ready', action });
      const rawToken = result.credential?.rawToken;
      if (rawToken) {
        setLink(order.id, deliveryLinkUrl(window.location.origin, rawToken));
        show(action === 'rotate'
          ? 'Enlace reemitido. El enlace anterior dejó de ser válido.'
          : 'Enlace de reparto emitido. Copialo ahora; no se volverá a mostrar.', 'success');
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo emitir el enlace de reparto.';
      show(message, 'error');
      if (error instanceof ApiClientError && error.status === 409) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
        show('El pedido cambió. La lista fue actualizada antes de reintentar.', 'error');
      }
    } finally {
      setActionId(null);
    }
  }, [queryClient, show, tokenMutation, setLink]);

  const setPreparation = useCallback(async (order: OrderView, task: PreparationItemView, status: PreparationState) => {
    setBusyTaskId(task.id);
    try {
      await apiRequest(`/api/orders/${order.id}/items/${task.id}/preparation`, { method: 'POST', body: jsonBody({ status }) });
      show(status === 'completado' ? `Tarea del pedido #${String(order.correlation).padStart(2, '0')} lista.` : 'Estado de preparación actualizado.', 'success');
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo actualizar la tarea.', 'error');
    } finally {
      setBusyTaskId(null);
    }
  }, [queryClient, show]);

  if (loading) return <LoadingScreen label="Abriendo reparto…" />;

  return (
    <div>
      <PageHeader
        title={isModeB ? 'Reparto · Preparación' : 'Reparto'}
        description={isModeB
          ? 'Cola de preparación de reparto en tiempo real. Marcar tareas como listas para avanzar pedidos de delivery.'
          : 'Pedidos de delivery listos para entregar: verifica el contenido y envía el enlace de seguimiento al cliente.'}
        action={(
          <div className="flex flex-wrap items-center gap-2">
            <span className={cx('inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-bold', streamStatus === 'connected' ? 'bg-emerald-100 text-emerald-800' : streamStatus === 'error' ? 'bg-rose-100 text-rose-800' : 'bg-slate-200 text-slate-700')}>
              <span className={cx('h-2 w-2 rounded-full', streamStatus === 'connected' ? 'bg-emerald-500' : streamStatus === 'error' ? 'bg-rose-500' : 'bg-slate-500')} />
              {streamStatus === 'connected' ? 'Tiempo real activo' : streamStatus === 'error' ? 'Reconectando' : 'Conectando'}
            </span>
            <Button variant="secondary" busy={refreshing} onClick={() => void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all })}>Actualizar</Button>
          </div>
        )}
      />

      {isModeB ? (
        <>
          <Card className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto] lg:items-end">
            <HeroSelectField
              label="Sucursal"
              value={branchId || null}
              isDisabled={!canChooseBranch}
              placeholder="Sin sucursales activas"
              onChange={(value) => setBranchId(String(value ?? ''))}
              options={branches.map((branch) => ({ id: branch.id, label: branch.name }))}
            />
            <HeroSelectField
              label="Zona de preparación"
              value={zone || null}
              placeholder="Todas las zonas"
              onChange={(value) => setZone(String(value ?? ''))}
              options={zones.map((value) => ({ id: value, label: value }))}
            />
            <div className="rounded-xl bg-slate-950 px-5 py-3 text-center text-white">
              <div className="text-xs text-slate-400">Pedidos en cola</div>
              <div className="text-2xl font-black">{readyOrders.length}</div>
            </div>
          </Card>
          <section>
            <h2 className="mb-3 text-lg font-black text-slate-950">Cola de preparación de reparto</h2>
            <DeliveryPrepQueue branchId={branchId} zone={zone} busyTaskId={busyTaskId} onStatus={setPreparation} />
          </section>
        </>
      ) : (
        <>
          <Card className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <HeroSelectField
              label="Sucursal"
              value={branchId || null}
              isDisabled={!canChooseBranch}
              placeholder="Sin sucursales activas"
              onChange={(value) => setBranchId(String(value ?? ''))}
              className="min-w-64"
              options={branches.map((branch) => ({ id: branch.id, label: branch.name }))}
            />
            <div className="flex gap-4 text-sm"><div><span className="text-slate-500">Listos para reparto</span><strong className="ml-2 text-lg">{readyOrders.length}</strong></div></div>
          </Card>

          <section>
            <h2 className="mb-3 text-lg font-black text-slate-950">Pedidos listos para entregar</h2>
            {readyOrders.length ? <div className="grid gap-4">{readyOrders.map((order) => {
              const link = links[order.id];
              const busy = actionId === order.id;
              return (
                <Card key={order.id} className="grid gap-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="grid h-16 min-w-16 place-items-center rounded-2xl bg-slate-950 px-3 text-white shadow-sm">
                        <div className="text-center"><div className="text-[10px] font-bold uppercase tracking-[.2em] text-slate-400">Pedido</div><div className="text-2xl font-black">#{String(order.correlation).padStart(2, '0')}</div></div>
                      </div>
                      <div>
                        <h3 className="text-lg font-black">{order.customerName}</h3>
                        <p className="text-sm text-slate-500">☎ {order.customerPhone}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2"><Badge tone="amber">Listo para entregar</Badge><div className="text-right"><div className="text-xl font-black">{formatUsd(order.totalUsdCents)}</div><div className="text-sm font-bold text-slate-500">{formatLocalMinor(order.totalLocalMinor, order.localCurrency, order.currencyDecimals, order.currencySymbol)}</div></div></div>
                  </div>
                  {order.deliveryAddress && <p className="rounded-xl bg-slate-50 p-3 text-sm"><strong>Dirección:</strong> {order.deliveryAddress}</p>}
                  {order.confirmedMapsUrl && <p className="break-all rounded-xl bg-slate-50 p-3 text-sm"><strong>Ubicación confirmada:</strong> <a className="font-bold text-sky-700 underline" href={order.confirmedMapsUrl} target="_blank" rel="noreferrer">{order.confirmedMapsUrl}</a></p>}
                  <div className="grid gap-2">
                    <h4 className="text-sm font-black uppercase tracking-wide text-slate-500">Contenido del pedido</h4>
                    {order.items.map((item) => <DeliveryItemSnapshot key={item.id} item={item} />)}
                  </div>
                  <div className="grid gap-2 rounded-2xl bg-slate-50 p-3">
                    <div className="flex flex-wrap gap-2">
                      <Button disabled={!order.confirmedMapsUrl} busy={busy} onClick={() => void issueToken(order, link ? 'rotate' : 'issue')}>{link ? 'Reemitir enlace' : 'Emitir enlace'}</Button>
                      <Button variant="secondary" disabled={!order.confirmedMapsUrl} busy={busy} onClick={() => void issueToken(order, 'rotate')}>Rotar enlace</Button>
                      <Button variant="secondary" disabled={!link} onClick={() => void copyLink(order.id)}>Copiar enlace</Button>
                      {link && <a href={deliveryWhatsAppUrl(order, link)} target="_blank" rel="noreferrer" className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">◉ WhatsApp</a>}
                    </div>
                    {!link && <p className="text-sm text-slate-500">{order.confirmedMapsUrl ? 'Emití el enlace para poder enviarlo por WhatsApp; el token se muestra una sola vez.' : 'Recepción debe confirmar la ubicación antes de emitir el enlace.'}</p>}
                    {link && <p className="break-all text-sm text-slate-600"><strong>Enlace nuevo:</strong> {link}</p>}
                  </div>
                </Card>
              );
            })}</div> : <EmptyState title="Sin pedidos listos para reparto" description="Cuando cocina marque un pedido de delivery como listo, aparecerá aquí en tiempo real con su contenido verificado." />}
          </section>
        </>
      )}
    </div>
  );
}
